import { DRIVE_ERROR_MESSAGES } from '../files/drive';
import type { Tables } from '../../types/database.types';

/**
 * The supplier review branch (handoff 32-37), Phase 6 Stage 2 data layer.
 * TAP-side surfaces arrive in Stage 3, the supplier portal in Stage 4; both
 * sit on these shapes.
 */

export type SupplierReview = Tables<'supplier_reviews'>;
export type SupplierReviewStatus = Tables<'supplier_review_statuses'>;
export type SupplierReviewMember = Tables<'supplier_review_members'>;
export type SampleSubmission = Tables<'sample_submissions'>;
export type SamplePhoto = Tables<'sample_photos'>;
export type SupplierReviewComment = Tables<'supplier_review_comments'>;

/** The frozen machine handles of the seeded statuses (handoff 34). */
export type ReviewStatusKey =
  | 'awaiting_supplier'
  | 'sample_in_progress'
  | 'ready_for_tap_review'
  | 'revision_requested'
  | 'approved_for_production'
  | 'on_hold';

/** A person surfaced by the review_people bridge (names only, never rows). */
export interface ReviewPerson {
  user_id: string;
  display_name: string;
  email: string;
  is_supplier: boolean;
}

/** A supplier user offered by the section 33 picker. */
export interface SupplierCandidate {
  user_id: string;
  display_name: string;
  email: string;
}

export interface OpenReviewInput {
  taskId: string;
  projectId: string;
  supplierOrganizationId: string;
  sharedTitle: string;
  instructions: string;
  reviewDueDate: string | null;
  openedBy: string;
  /** Selected supplier users (handoff 33); added right after the insert. */
  memberUserIds: string[];
}

export interface ApproveReviewInput {
  reviewId: string;
  approvedBy: string;
  approvedDesignFileId: string | null;
  approvedSampleSubmissionId: string | null;
  approvalNote: string;
  /** Resolved from the statuses list by system_key approved_for_production. */
  approvedStatusId: string;
}

/** A submission with its photos, newest version first. */
export interface SubmissionWithPhotos {
  submission: SampleSubmission;
  photos: SamplePhoto[];
}

export const REVIEW_INSTRUCTIONS_MAX_LENGTH = 10000;
export const REVIEW_NOTE_MAX_LENGTH = 2000;

/** Finds a seeded status by its frozen machine handle. */
export function statusByKey(
  statuses: SupplierReviewStatus[],
  key: ReviewStatusKey,
): SupplierReviewStatus | undefined {
  return statuses.find((status) => status.system_key === key);
}

/** Active statuses in display order for pickers and badges. */
export function activeReviewStatuses(statuses: SupplierReviewStatus[]): SupplierReviewStatus[] {
  return statuses
    .filter((status) => status.is_active)
    .slice()
    .sort((left, right) => left.position - right.position);
}

/** Groups a flat photo list under its submissions, newest version first. */
export function groupSubmissions(
  submissions: SampleSubmission[],
  photos: SamplePhoto[],
): SubmissionWithPhotos[] {
  return submissions
    .slice()
    .sort((left, right) => right.version_number - left.version_number)
    .map((submission) => ({
      submission,
      photos: photos.filter((photo) => photo.submission_id === submission.id),
    }));
}

/**
 * Translates the guard refusals a person can actually meet into sentences.
 * Anything unrecognised falls back to a retry line.
 */
export function reviewErrorMessage(message: string): string {
  if (message.includes('supplier_reviews_task_key')) {
    return 'This task already has a supplier review.';
  }
  if (message.includes('supplier_organization_inactive')) {
    return 'That supplier organization is inactive.';
  }
  if (message.includes('member_must_be_active_supplier_of_review_org')) {
    return 'Only active supplier users of the review’s organization can be selected.';
  }
  if (message.includes('approval_requires_project_manager')) {
    return 'Only the project owner or a TAP admin can decide an approval.';
  }
  if (message.includes('approved_versions_cannot_be_silently_replaced')) {
    return 'This review is approved. Reopen it before changing the approved versions.';
  }
  if (message.includes('submitted_versions_are_frozen')) {
    return 'This sample version was already submitted and can no longer change.';
  }
  if (message.includes('review_not_permitted')) {
    return 'You do not have permission to change this review.';
  }
  const driveMessage = DRIVE_ERROR_MESSAGES[message];
  if (driveMessage !== undefined) {
    return driveMessage;
  }
  if (message.startsWith('drive_error_')) {
    return 'Google Drive refused the upload. Try again.';
  }
  return 'That change could not be saved. Try again.';
}
